import { useCallback, useEffect, useMemo, useState } from "react";
import { Shield, UserMinus, UserPlus } from "lucide-react";
import Button from "../ui/Button";
import Select from "../ui/Select";
import SlaJourneyFields from "./SlaJourneyFields";
import { fetchTeamMembers } from "../../services/teamMembersApi";
import {
  addInstitutionOpsAssignment,
  createInstitutionSlaSetting,
  fetchInstitutionOpsAssignments,
  fetchInstitutionSlaSettings,
  removeInstitutionOpsAssignment,
} from "../../services/institutionScopeApi";
import { normalizeRole, ROLES } from "../../config/roles";
import { showError, showSuccess } from "../../utils/toast";
import { formatDateTime } from "../../data/mock/dashboardData";
import usePermissions from "../../hooks/usePermissions";

/** Super admin and commercial executive assign managers and edit provider SLA. */
function canAssignInstitutions(user) {
  const role = normalizeRole(user?.role);
  return role === ROLES.SUPER_ADMIN || role === ROLES.EXECUTIVE_MANAGER;
}

function eligibleCallCenterManagers(members) {
  return (members || []).filter((m) => {
    if (m.status === "deleted" || m.status === "inactive") return false;
    return normalizeRole(m.role || m.role_slug) === "call_center_manager";
  });
}

export default function InstitutionOpsScopePanel({
  token,
  institutionId,
  institutionName,
  useLiveApi,
  writable = true,
}) {
  const { user } = usePermissions();
  const canSee = canAssignInstitutions(user);
  const canManage = canSee && writable;

  const [assignments, setAssignments] = useState([]);
  const [sla, setSla] = useState({ effective: null, history: [] });
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickAdminId, setPickAdminId] = useState("");
  const [slaDraft, setSlaDraft] = useState({
    contract_sla_days: 20,
    manager_assignment_days: 5,
    agent_engagement_days: 2,
  });
  const [savingSla, setSavingSla] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    if (!useLiveApi || !token || !institutionId) return;
    setLoading(true);
    try {
      const [rows, slaData, members] = await Promise.all([
        fetchInstitutionOpsAssignments(token, institutionId),
        fetchInstitutionSlaSettings(token, institutionId),
        fetchTeamMembers(token),
      ]);
      setAssignments(rows);
      setSla(slaData);
      setTeam(eligibleCallCenterManagers(members));
      if (slaData.effective) {
        setSlaDraft({
          contract_sla_days: slaData.effective.contract_sla_days ?? 20,
          manager_assignment_days: slaData.effective.manager_assignment_days ?? 5,
          agent_engagement_days: slaData.effective.agent_engagement_days ?? 2,
        });
      }
    } catch (err) {
      showError(err.message || "Could not load ops scope.");
    } finally {
      setLoading(false);
    }
  }, [token, institutionId, useLiveApi]);

  useEffect(() => {
    load();
  }, [load]);

  const assignedIds = useMemo(
    () => new Set(assignments.map((a) => String(a.admin_id))),
    [assignments]
  );

  const addOptions = useMemo(
    () =>
      team
        .filter((m) => m.id && !assignedIds.has(String(m.id)))
        .map((m) => ({
          value: String(m.id),
          label: m.name || m.email || m.id,
        })),
    [team, assignedIds]
  );

  const handleAdd = async () => {
    if (!pickAdminId) return;
    const manager = addOptions.find((option) => option.value === String(pickAdminId));
    setConfirm({
      type: "assign",
      adminId: pickAdminId,
      name: manager?.label || "this manager",
    });
  };

  const handleRemove = (row) => {
    setConfirm({
      type: "reassign",
      adminId: row.admin_id,
      name: row.name || row.email || "this manager",
    });
  };

  const confirmAction = async () => {
    if (!confirm) return;
    try {
      if (confirm.type === "assign") {
        await addInstitutionOpsAssignment(token, institutionId, confirm.adminId);
        setPickAdminId("");
        showSuccess(`${confirm.name} can now see and work ${institutionName || "this institution"}.`);
      } else {
        await removeInstitutionOpsAssignment(token, institutionId, confirm.adminId);
        showSuccess(`${confirm.name} no longer sees ${institutionName || "this institution"}.`);
      }
      setConfirm(null);
      await load();
    } catch (err) {
      showError(err.message || "Could not update the manager assignment.");
    }
  };

  const handleSlaSave = async () => {
    setSavingSla(true);
    try {
      await createInstitutionSlaSetting(token, institutionId, {
        contract_sla_days: Number(slaDraft.contract_sla_days) || null,
        manager_assignment_days: Number(slaDraft.manager_assignment_days),
        agent_engagement_days: Number(slaDraft.agent_engagement_days),
      });
      showSuccess("SLA settings updated.");
      await load();
    } catch (err) {
      showError(err.message || "Could not update SLA.");
    } finally {
      setSavingSla(false);
    }
  };

  if (!canSee) {
    return null;
  }

  if (!useLiveApi) {
    return (
      <section className="rounded-xl border border-primary/[0.07] bg-white p-5">
        <h2 className="text-sm font-bold text-primary">Ops scope & SLA</h2>
        <p className="mt-2 text-xs text-primary/45">Connect to the API to manage manager scope and SLA.</p>
      </section>
    );
  }

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <section className="rounded-xl border border-primary/[0.07] bg-white p-5 shadow-[0_1px_2px_rgba(127,29,29,0.04)]">
        <div className="flex items-start gap-2">
          <Shield className="mt-0.5 h-4 w-4 text-primary/50" />
          <div>
            <h2 className="text-sm font-bold text-primary">Call-centre managers</h2>
            <p className="mt-0.5 text-xs text-primary/45">
              Assigning a call centre manager gives them this institution: its cases, customers, payments,
              and reports. Reassigning removes that access. Cases already with agents stay with those agents.
            </p>
          </div>
        </div>

        {loading ? (
          <p className="mt-4 text-xs text-primary/45">Loading…</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {assignments.length === 0 ? (
              <li className="text-xs text-primary/40">No managers assigned yet.</li>
            ) : (
              assignments.map((row) => (
                <li
                  key={row.admin_id}
                  className="flex items-center justify-between gap-2 rounded-lg bg-primary/[0.02] px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-primary">{row.name || row.email}</p>
                    <p className="truncate text-[11px] text-primary/45">Call centre manager</p>
                    <p className="mt-1 text-[11px] text-primary/55">
                      Assigned by {row.assigned_by_name || "—"}
                      {row.assigned_at ? ` · ${formatDateTime(row.assigned_at)}` : ""}
                    </p>
                  </div>
                  {canManage ? (
                    <button
                      type="button"
                      onClick={() => handleRemove(row)}
                      className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-bold text-red-700 hover:bg-red-50"
                    >
                      <UserMinus className="h-3.5 w-3.5" />
                      Reassign
                    </button>
                  ) : null}
                </li>
              ))
            )}
          </ul>
        )}

        {canManage ? (
          <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-primary/[0.06] pt-4">
            <div className="min-w-[200px] flex-1">
              <Select
                label="Assign call-centre manager"
                value={pickAdminId}
                onChange={setPickAdminId}
                options={[{ value: "", label: "Select manager…" }, ...addOptions]}
              />
            </div>
            <Button variant="outline" size="sm" disabled={!pickAdminId} onClick={handleAdd}>
              <UserPlus className="h-4 w-4" />
              Assign
            </Button>
          </div>
        ) : null}

        {confirm ? (
          <div className="mt-4 rounded-xl border border-primary/10 bg-primary/[0.03] p-3">
            <p className="text-sm font-bold text-primary">
              {confirm.type === "assign" ? "Assign this call centre manager?" : "Reassign this call centre manager?"}
            </p>
            <p className="mt-1 text-xs leading-relaxed text-primary/60">
              {confirm.type === "assign"
                ? `Assigning ${confirm.name} gives them ${institutionName || "this institution"} from now. They will see its cases, customers, payments, SMS, and reports, and they can assign those cases to call centre agents.`
                : `Reassigning removes ${confirm.name} from ${institutionName || "this institution"}. They will no longer see its cases, payments, or reports. Cases already assigned to call centre agents stay with those agents. You can assign a different manager after this.`}
            </p>
            <div className="mt-3 flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button variant={confirm.type === "assign" ? "accent" : "outline"} size="sm" onClick={confirmAction}>
                {confirm.type === "assign" ? "Confirm assign" : "Confirm reassign"}
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <section className="rounded-xl border border-primary/[0.07] bg-white p-5 shadow-[0_1px_2px_rgba(127,29,29,0.04)]">
        <h2 className="text-sm font-bold text-primary">SLA settings</h2>
        <p className="mt-0.5 text-xs text-primary/45">
          KPI targets for manager assignment and agent first contact on this institution.
        </p>
        {sla.effective ? (
          <p className="mt-2 text-[11px] text-primary/50">
            Current: assign {sla.effective.manager_assignment_days}d · contact{" "}
            {sla.effective.agent_engagement_days}d
            {sla.effective.contract_sla_days ? ` · contract ${sla.effective.contract_sla_days}d` : ""}
          </p>
        ) : null}

        {canManage ? (
          <>
            <div className="mt-4">
              <SlaJourneyFields
                values={slaDraft}
                onChange={setSlaDraft}
                institutionName={institutionName}
              />
            </div>
            <Button variant="accent" size="sm" className="mt-4" loading={savingSla} onClick={handleSlaSave}>
              Save new SLA version
            </Button>
          </>
        ) : sla.effective ? (
          <p className="mt-3 text-xs text-primary/55">
            Contract {sla.effective.contract_sla_days ?? "—"} days · assign within{" "}
            {sla.effective.manager_assignment_days} · contact within {sla.effective.agent_engagement_days}.
          </p>
        ) : (
          <p className="mt-3 text-xs text-primary/45">No SLA version on file.</p>
        )}
      </section>
    </div>
  );
}
